/* agent-dock.js — the persistent agent dock.
 *
 * A glanceable, always-present surface for the live agent run, sitting just
 * above the composer:
 *   • banner   — autonomy mode (reuses describeAutonomy) + Stop while running
 *   • plan     — the agent's TodoWrite checklist (intent)
 *   • timeline — legible tool steps (verb · target · why, risk-accented)
 *   • now-line — what it's doing right now
 *
 * Idle → a thin banner-only bar. Running → expanded. Driven entirely by
 * runAtom + autonomyAtom (one-way: atoms → DOM), so nothing pokes it directly.
 */

import { effect } from "./store.js";
import { esc, escAttr } from "./state.js";
import { renderDiffInto } from "./diff-render.js";
import { runAtom, autonomyAtom, describeAutonomy, toggleStepDiff } from "./agent-store.js";

let _cancelHandler = null;
let _wired = false;

/** Wire the dock once. `onCancel` is invoked when the user clicks Stop. */
export function initAgentDock({ onCancel } = {}) {
  _cancelHandler = onCancel || null;
  if (_wired) return;
  _wired = true;
  // One effect tracks both atoms; renderDock re-reads the live element each time.
  effect(() => {
    runAtom.get();
    autonomyAtom.get();
    renderDock();
  });
}

function _fmtMs(ms) {
  if (ms == null) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function _planGlyph(status) {
  if (status === "completed") return "☑";
  if (status === "in_progress") return "◐";
  return "☐";
}

function _stepGlyph(status) {
  if (status === "ok") return "✓";
  if (status === "err") return "✗";
  return "◐";
}

function renderPlan(plan) {
  if (!plan.length) return "";
  const items = plan
    .map((t) => {
      const st = t.status || "pending";
      const text = t.content || t.text || "";
      return `<li class="dock-plan-item st-${esc(st)}"><span class="dock-plan-glyph">${_planGlyph(st)}</span><span class="dock-plan-text">${esc(text)}</span></li>`;
    })
    .join("");
  return `<div class="dock-section dock-plan"><div class="dock-section-title">Plan</div><ul class="dock-plan-list">${items}</ul></div>`;
}

function renderFileWriteStep(s) {
  const caret = s.expanded ? "▾" : "▸";
  return `<div class="dock-step file-write risk-mutate" data-step-id="${escAttr(s.id)}" role="button" tabindex="0" title="Toggle diff">
      <span class="dock-step-glyph">✎</span>
      <span class="dock-step-verb">${esc(s.verb || "Wrote")}</span>
      ${s.target ? `<code class="dock-step-target">${esc(s.target)}</code>` : ""}
      <span class="dock-step-diff-toggle">${caret} diff</span>
    </div>${s.expanded ? `<div class="dock-step-diff" data-diff-for="${escAttr(s.id)}"></div>` : ""}`;
}

function renderSteps(steps) {
  if (!steps.length) return "";
  const rows = steps
    .map((s) => {
      if (s.kind === "file_write") return renderFileWriteStep(s);
      const why = s.why ? `<span class="dock-step-why">— ${esc(s.why)}</span>` : "";
      const ms = s.ms != null ? `<span class="dock-step-ms">${_fmtMs(s.ms)}</span>` : "";
      return `<div class="dock-step st-${esc(s.status)} risk-${esc(s.risk || "read")}">
        <span class="dock-step-glyph">${_stepGlyph(s.status)}</span>
        <span class="dock-step-verb">${esc(s.verb || "")}</span>
        ${s.target ? `<code class="dock-step-target">${esc(s.target)}</code>` : ""}
        ${why}${ms}
      </div>`;
    })
    .join("");
  return `<div class="dock-section dock-timeline">${rows}</div>`;
}

function renderDock() {
  const dock = document.getElementById("agentDock");
  if (!dock || !dock.isConnected) return;

  const run = runAtom.peek();
  const auto = describeAutonomy(autonomyAtom.peek());
  const running = run.status === "running";

  dock.hidden = false; // the thin autonomy bar is always useful
  dock.className = `agent-dock ${running ? "running" : "idle"}`;

  const cancelHtml = running
    ? `<button class="dock-cancel" id="dockCancel" type="button" title="Stop the agent">Stop</button>`
    : "";
  const nowHtml =
    running && run.nowLabel
      ? `<div class="dock-now"><span class="dock-spinner"></span><span>${esc(run.nowLabel)}</span></div>`
      : "";

  dock.innerHTML = `
    <div class="dock-banner tone-${auto.tone}">
      <span class="autonomy-dot dot-${auto.dot}"></span>
      <span class="dock-banner-label">${esc(auto.label)}</span>
      <span class="dock-banner-spacer"></span>
      ${cancelHtml}
    </div>
    ${renderPlan(run.plan)}
    ${renderSteps(run.steps)}
    ${nowHtml}
  `;

  const cancelBtn = dock.querySelector("#dockCancel");
  if (cancelBtn && _cancelHandler) {
    cancelBtn.addEventListener("click", () => _cancelHandler());
  }

  // Inline file-write diffs are temporarily disabled: dock steps are now a
  // stateless projection of the parts model, so per-step diff state no longer
  // lives on the step. `toggleStepDiff` is a no-op until a later task restores
  // this by reading the diff from the tool part. The loop below is currently
  // inert (no step ever has `expanded`/`diff` set).
  for (const s of run.steps) {
    if (s.kind !== "file_write" || !s.expanded) continue;
    const sel = window.CSS && CSS.escape ? CSS.escape(s.id) : s.id;
    const container = dock.querySelector(`.dock-step-diff[data-diff-for="${sel}"]`);
    if (!container) continue;
    if (s.diffLoading) container.innerHTML = `<div class="diff-empty">Loading diff…</div>`;
    else if (s.diffError) container.innerHTML = `<div class="diff-empty diff-error">${esc(s.diffError)}</div>`;
    else if (s.diff) renderDiffInto(container, s.diff);
    else container.innerHTML = `<div class="diff-empty">No diff loaded.</div>`;
  }
  // Clicking a file-write step toggles its inline diff (lazy-fetched once).
  dock.querySelectorAll(".dock-step.file-write").forEach((row) => {
    row.addEventListener("click", () => toggleStepDiff(row.dataset.stepId));
  });
}
